import { type LucideIcon, MapPin, PenLine, Pencil, TriangleAlert, UserPlus } from 'lucide-react';
import {
  type MouseEvent,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertDialog, Button } from './ui';

/*
 * Защита несохранённого (FR-007, FR-008; contracts/ui-panel.md, «Закрытие и несохранённое», «Окна»; research R13).
 * Роутер — BrowserRouter, `useBlocker` недоступен, поэтому переходы идут через контекст: кто уводит со страницы
 * (пункт меню, нижняя навигация, смена роли, закрытие панели, другая ячейка), вызывает `guard(next)`.
 * Есть несохранённое — окно «Сохранить …?»; нет — `next()` сразу. Закрытие вкладки — `beforeunload`.
 */

/**
 * Что не сохранено: ответ, ответ сразу нескольким, правка часов, комментарий «Уточнить», отмеченные люди, изменение
 * правила расчёта (004, T063: карточка правила в изменении, «Сохранить в черновик»), ответы шага подключения площадки и
 * окно «Подключить площадку» (007, T047).
 */
export type UnsavedKind = 'answer' | 'bulk' | 'edit' | 'clarify' | 'people' | 'rule' | 'answers' | 'site';

export interface UnsavedState {
  kind: UnsavedKind;
  /** Что не сохранено, одной фразой: «Тестов Т., 3 окт.: выход в 19:00». */
  summary: string;
  /** Ввод дописан и его можно сохранить. Нет — окно предлагает «Дописать» и «Не сохранять». */
  canSave: boolean;
  /**
   * Сохранить. Promise — окно ждёт; успех — переход, ошибка — перехода нет, ввод остаётся.
   * Ошибку показывает сама страница (`showError` с `retry`).
   */
  save: () => unknown;
  /** Сбросить ввод при «Не сохранять». Не нужно, если переход и так закроет форму. */
  discard?: () => void;
  /** Подпись кнопки сохранения вместо обычной: «Добавить 3». */
  saveLabel?: string;
}

interface Text {
  /** Заголовок окна, если ввод дописан. */
  title: string;
  save: string;
  /** Заголовок окна, если ввод не дописан. */
  unfinished: string;
  discard: string;
  /** Второе предложение текста, если ввод дописан. */
  note: string;
  icon: LucideIcon;
}

const SAVED_NOT = 'Изменение ещё не сохранено.';

const TEXT: Record<UnsavedKind, Text> = {
  answer: {
    title: 'Сохранить ответ?',
    save: 'Сохранить ответ',
    unfinished: 'Ответ не дописан',
    discard: 'Не сохранять',
    note: SAVED_NOT,
    icon: Pencil,
  },
  bulk: {
    title: 'Сохранить ответ?',
    save: 'Сохранить ответ',
    unfinished: 'Ответ не дописан',
    discard: 'Не сохранять',
    note: SAVED_NOT,
    icon: Pencil,
  },
  edit: {
    title: 'Сохранить правку?',
    save: 'Сохранить правку',
    unfinished: 'Правка не дописана',
    discard: 'Не сохранять',
    note: SAVED_NOT,
    icon: Pencil,
  },
  clarify: {
    title: 'Сохранить «Уточнить»?',
    save: 'Сохранить «Уточнить»',
    unfinished: 'Комментарий «Уточнить» не дописан',
    discard: 'Не сохранять',
    note: SAVED_NOT,
    icon: Pencil,
  },
  people: {
    title: 'Добавить выбранных?',
    save: 'Добавить выбранных',
    unfinished: 'Выбор не закончен',
    discard: 'Не добавлять',
    note: 'В табель они ещё не добавлены.',
    icon: UserPlus,
  },
  rule: {
    title: 'Сохранить в черновик?',
    save: 'Сохранить в черновик',
    unfinished: 'Значение не дописано',
    discard: 'Не сохранять',
    note: SAVED_NOT,
    icon: Pencil,
  },
  answers: {
    title: 'Сохранить ответы?',
    save: 'Сохранить ответы',
    unfinished: 'Ответы не дописаны',
    discard: 'Не сохранять',
    note: SAVED_NOT,
    icon: Pencil,
  },
  site: {
    title: 'Подключить площадку?',
    save: 'Подключить площадку',
    unfinished: 'Данные площадки не дописаны',
    discard: 'Не подключать',
    note: 'Площадка ещё не подключена.',
    icon: MapPin,
  },
};

const sentence = (s: string) => (/[.!?…]$/.test(s.trim()) ? s.trim() : `${s.trim()}.`);

export interface UnsavedGuardApi {
  /** Есть несохранённое. */
  dirty: boolean;
  /** Отметить несохранённое; `null` — всё сохранено. */
  setDirty: (state: UnsavedState | null) => void;
  /** Сброс. С `owner` — только если несохранённое всё ещё его. */
  clearDirty: (owner?: UnsavedState) => void;
  /** Уйти: без несохранённого — `next()` сразу; есть — окно «Сохранить …?», `next()` — после ответа. */
  guard: (next: () => void) => void;
}

const UnsavedContext = createContext<UnsavedGuardApi>({
  dirty: false,
  setDirty: () => {},
  clearDirty: () => {},
  guard: (next) => next(),
});

export function useUnsavedGuard(): UnsavedGuardApi {
  return useContext(UnsavedContext);
}

/**
 * Переходы каркаса и навигации «Настроек» — через защиту несохранённого: пункт меню, нижняя навигация, крошки, логотип,
 * «Новый табель», смена роли и объекта, раздел настроек. Ссылка с клавишей-модификатором открывается как обычно
 * (новая вкладка). `go(to)` — переход кнопкой, `link(to)` — обработчик нажатия ссылки.
 */
export function useGuardedNav() {
  const { dirty, guard } = useUnsavedGuard();
  const navigate = useNavigate();
  const go = (to: string) => guard(() => navigate(to));
  const link = (to: string) => (e: MouseEvent<HTMLAnchorElement>) => {
    if (!dirty || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    go(to);
  };
  return { go, link };
}

/** Защита несохранённого для страниц внутри `AppShell` (подключена там). */
export function UnsavedGuardProvider({ children }: { children: ReactNode }) {
  const current = useRef<UnsavedState | null>(null);
  const [dirty, setFlag] = useState(false);
  /** Что спрашивает окно (снимок на момент перехода); `null` — окно закрыто. */
  const [ask, setAsk] = useState<UnsavedState | null>(null);
  const [busy, setBusy] = useState(false);
  const waiting = useRef<(() => void) | null>(null);
  const proceed = useRef<(() => void) | null>(null);

  const setDirty = useCallback((s: UnsavedState | null) => {
    current.current = s;
    setFlag(s !== null);
  }, []);
  const clearDirty = useCallback(
    (owner?: UnsavedState) => {
      if (owner && current.current !== owner) return;
      setDirty(null);
    },
    [setDirty],
  );
  const guard = useCallback((next: () => void) => {
    const s = current.current;
    if (!s) {
      next();
      return;
    }
    if (waiting.current) return; // окно уже открыто
    waiting.current = next;
    setAsk(s);
  }, []);

  const close = (go: boolean) => {
    proceed.current = go ? waiting.current : null;
    waiting.current = null;
    setAsk(null);
  };
  // Переход — после закрытия окна: фокус сначала уходит из окна, потом страница закрывает панель и возвращает его.
  useEffect(() => {
    if (ask) return;
    const next = proceed.current;
    proceed.current = null;
    next?.();
  }, [ask]);

  const discard = () => {
    (current.current ?? ask)?.discard?.();
    setDirty(null);
    close(true);
  };
  const save = async () => {
    const s = current.current ?? ask;
    if (!s) return;
    setBusy(true);
    try {
      await s.save();
    } catch {
      setBusy(false);
      close(false);
      return;
    }
    setBusy(false);
    setDirty(null);
    close(true);
  };

  // Закрытие или перезагрузка вкладки при несохранённом — вопрос браузера.
  useEffect(() => {
    if (!dirty) return;
    const onUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [dirty]);

  const api = useMemo(() => ({ dirty, setDirty, clearDirty, guard }), [dirty, setDirty, clearDirty, guard]);
  const t = ask ? TEXT[ask.kind] : null;

  return (
    <UnsavedContext.Provider value={api}>
      {children}
      <AlertDialog
        open={ask !== null}
        onClose={() => {
          if (!busy) close(false);
        }}
        tone="warn"
        icon={ask && !ask.canSave ? TriangleAlert : (t?.icon ?? Pencil)}
        title={ask && t ? (ask.canSave ? t.title : t.unfinished) : ''}
        description={
          ask && t
            ? `${sentence(ask.summary)} ${ask.canSave ? t.note : 'Если уйти, изменение пропадёт.'}`
            : ''
        }
      >
        {ask && t && (
          <>
            <Button variant="ghost" disabled={busy} onClick={discard}>
              {t.discard}
            </Button>
            <span className="flex-1 max-sm:hidden" aria-hidden />
            {ask.canSave ? (
              <>
                <Button disabled={busy} onClick={() => close(false)}>
                  Отмена
                </Button>
                <Button variant="primary" disabled={busy} data-autofocus onClick={() => void save()}>
                  {ask.saveLabel ?? t.save}
                </Button>
              </>
            ) : (
              <Button variant="primary" data-autofocus onClick={() => close(false)}>
                Дописать
              </Button>
            )}
          </>
        )}
      </AlertDialog>
    </UnsavedContext.Provider>
  );
}

/**
 * Несохранённое формы: `state` — пока ввод не сохранён, `null` — сохранён или пуст. Снятая с экрана форма
 * несохранённое снимает. `save` и `discard` берутся последние, без лишних обновлений контекста.
 */
export function useUnsavedChanges(state: UnsavedState | null): void {
  const { setDirty, clearDirty } = useUnsavedGuard();
  const latest = useRef(state);
  latest.current = state;
  const mine = useRef<UnsavedState | null>(null);
  const key = state ? [state.kind, state.summary, state.canSave, state.saveLabel ?? ''].join('\u0000') : null;
  useEffect(() => {
    const s = latest.current;
    if (key === null || !s) {
      if (mine.current) clearDirty(mine.current);
      mine.current = null;
      return;
    }
    const proxy: UnsavedState = {
      kind: s.kind,
      summary: s.summary,
      canSave: s.canSave,
      saveLabel: s.saveLabel,
      save: () => latest.current?.save(),
      discard: () => latest.current?.discard?.(),
    };
    mine.current = proxy;
    setDirty(proxy);
  }, [key, setDirty, clearDirty]);
  useEffect(
    () => () => {
      if (mine.current) clearDirty(mine.current);
    },
    [clearDirty],
  );
}

/** Пометка «Не сохранено» в подвале панели, пока есть несохранённое (FR-008). */
export function UnsavedMark() {
  return (
    <span className="inline-flex items-center gap-1.5 text-[12.5px] font-medium whitespace-nowrap text-warn">
      <PenLine size={13} strokeWidth={1.75} aria-hidden />
      Не сохранено
    </span>
  );
}
